import React, { useEffect, useState } from "react";
import {
  calculateDynamicWidth,
  getSanatizedCode,
  createColumnGroupingModel,
  getFieldToPrefixMapping,
  transformHeaderName,
  RecordType,
  calculateDaysInQueue,
  getFormattedDateTime,
  deduplicateByFieldProperty,
  excludeInternalResultColumns,
  getSecondaryMergeResourceName,
  getSecondaryMergeResources,
  SECONDARY_MERGE_RESOURCES_KEY,
} from "../../services/utils";
import RulesListDataTable from "../DataGrids/RulesListDataGrid";
import {
  Backdrop,
  Box,
  Button,
  Fade,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid-pro";
import {
  IconBoltType,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconFalsePositive,
  IconIncidentDetails,
  IconIncidentResources,
  IconSecondaryResourceSvg,
} from "../../common/utils/icons";
import useFetchMissingRecordsWithIssuesByExecutionId from "../../hooks/useFetchMissingRecordsWithIssuesByExecutionId";
import DashboardUserDetails from "../Molecules/Rule/DashboardUserDetails";
import IncidentUserandStatusDialog from "../Molecules/Rule/IncidentUserandStatusDialog";
import useFetchUsersList from "../../hooks/useFetchUsersList";
import Loader from "../Molecules/Loader/Loader";
import { processIssueAnalysisData } from "../../common/utils/commonFunctions";
import {
  ClassificationColumn,
  CommentColumn,
  CurrentStatusColumn,
  ReasonColumn,
  UserColumn,
} from "../../common/utils/commonActionsFields";
import { useAuth } from "../../contexts/AuthContext";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import { darkModeTokens } from "../../styles/Theme";

interface MissingRecordsDashboardTabProps {
  dashboardData: any;
  setMissingColumns: any;
  userDialogData?: any;
  setUserDialogData?: any;
}

const MissingRecordsDashboardTab = ({
  dashboardData,
  setMissingColumns,
  userDialogData,
  setUserDialogData,
}: MissingRecordsDashboardTabProps) => {
  const { hasPermission } = useAuth();
  const { mode } = useThemeMode();
  const isDark = mode === "dark";
  const canEditRuleExecutionIssues = hasPermission(
    "rule_execution_issues",
    "edit",
  );
  const [missingRowsData, setMissingRowsData] = useState<any>([]);
  const [missingColumnsData, setMissingColumnsData] = useState<any>([]);
  const [columnGroupingModel, setColumnGroupingModel] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMissingLoading, setIsMissingLoading] = useState<boolean>(false);
  //for pagination default values and states
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const { rowsPerPage, rowsPerPageOptions } = useRowsPerPage();
  const [allUsersList, setallUsersList] = useState<any>([]);
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1,
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  const [selectedIds, setSelectedIds] = useState<any>([]);
  const [keyColumns, setKeyColumns] = useState<any>([]);
  const [isAllColumnPinned, setIsAllColumnPinned] = useState(false);

  const [dashboardUserDetailsData, setDashboardUserDetailsData] = useState({
    status: false,
    assigned_user: "",
    issueId: null,
    isBackDropVisible: false,
  });

  const [usersList] = useFetchUsersList({
    setIsLoading,
  });
  useEffect(() => {
    setallUsersList(usersList);
  }, [usersList]);

  const [missingResultsRuleDashboardData] =
    useFetchMissingRecordsWithIssuesByExecutionId({
      setIsLoading: setIsMissingLoading,
      isMissingFetch: !dashboardData?.is_detailed_execution_data_available,
      currentRuleResultId: dashboardData?.id,
      page,
      pSize,
    });

  useEffect(() => {
    if (
      missingResultsRuleDashboardData?.items &&
      missingResultsRuleDashboardData?.items.length > 0
    ) {
      const keyColumns = Object.keys(
        missingResultsRuleDashboardData?.items[0]?.missing_record_details
          ?.comparison_keys,
      ).map((key, value) => key);

      setKeyColumns(keyColumns);
      setIsMissingLoading(false);
    }
  }, [missingResultsRuleDashboardData]);
  useEffect(() => {
    if (!missingResultsRuleDashboardData || !dashboardData) {
      return;
    }

    const columnGrouping = createColumnGroupingModel(
      dashboardData,
      missingResultsRuleDashboardData.items,
      "missing" as RecordType,
      isAllColumnPinned,
    );

    setColumnGroupingModel(columnGrouping);
  }, [dashboardData, missingResultsRuleDashboardData, isAllColumnPinned]);

  const getFormattedKeysPairs = (
    inputString: string | undefined,
    type?: "missing",
  ) => {
    const pairs: any = {};

    // Check if inputString is defined and is a string
    if (inputString && typeof inputString === "string") {
      inputString.split(",").forEach((pair) => {
        if (pair.trim() !== "None") {
          const [key, value] = pair.trim().split("=");
          pairs[key] = getSanatizedCode(value);
        }
      });
    }

    return pairs;
  };
  const getFormattedKeysPairs1 = (
    missing_in: any,
    false_positive_missings: any,
    resource_prefixes: any,
    type?: "missing",
  ) => {
    const pairs: any = {};
    if (!missing_in || !resource_prefixes) return {};

    const secondaryMergeResources = getSecondaryMergeResources(
      false_positive_missings,
    );

    resource_prefixes.forEach((prefix: string) => {
      if (getSecondaryMergeResourceName(secondaryMergeResources, prefix)) {
        pairs[prefix] = "secondaryfulfilled";
        return;
      }
      pairs[prefix] = missing_in.includes(prefix)
        ? false_positive_missings?.[prefix]
          ? "falsepositive"
          : "falsenegative"
        : "truepositive";
    });
    pairs[SECONDARY_MERGE_RESOURCES_KEY] = secondaryMergeResources;

    return pairs;
  };

  const getFormattedKeysPairs2 = (inputString: string, type?: "missing") => {
    const pairs: any = {};

    if (inputString && typeof inputString === "string") {
      inputString.split(",").forEach((pair) => {
        const [key, value] = pair.trim().split("=");

        if (key && value !== undefined) {
          pairs[key] = getSanatizedCode(value, ";");
        }
      });
    }
    return pairs;
  };

  const processRows = (rows: any[]) => {
    if (!rows) return;
    return rows.map((row: any) => {
      const updatedRow = { ...row };

      Object.keys(updatedRow).forEach((key) => {
        if (typeof updatedRow[key] === "string") {
          if (
            /\.0$/.test(updatedRow[key]) &&
            !/\.0[1-9]/.test(updatedRow[key])
          ) {
            updatedRow[key] = updatedRow[key].slice(0, -2);
          }
        }
      });

      return updatedRow;
    });
  };
  const getMissingRows = (
    missing_records_details: any,
    resource_prefixes?: any,
  ) => {
    const recordData =
      missing_records_details[0]?.missing_record_details?.record_snapshot
        ?.record_data;
    const rows: any = missing_records_details?.map((item: any, idx: any) => {
      const diffInDays = calculateDaysInQueue(item.first_occurrence);
      return {
        id: item?.id,
        rowId: item?.id,
        assigned_user: item?.assigned_user,
        current_status: item?.current_status,
        comment: item?.comment,
        first_occurrence: item?.first_occurrence,
        days_in_queue: diffInDays,
        issue_classification_id: item?.issue_classification_id,
        issue_reason_id: item?.issue_reason_id,
        ...(recordData
          ? { ...item?.missing_record_details?.record_snapshot?.record_data }
          : { ...item?.missing_record_details?.comparison_keys }),
        ...getFormattedKeysPairs1(
          item?.missing_record_details?.missing_in,
          item?.missing_record_details?.false_positive_missings,
          resource_prefixes,
          "missing",
        ),
        message: item?.missing_record_details?.error_message,
        ...item?.missing_record_details?.file_names,
      };
    });
    const processedRows = processRows(rows);
    setMissingRowsData(processedRows);
  };
  const handleIncidentResources = (id: number) => {
    const prefix = dashboardData?.additional_properties?.resource_prefixes;
    const rowData = missingResultsRuleDashboardData?.items.find(
      (item: any) => item.id === id,
    )?.missing_record_details?.record_snapshot?.record_data;

    const result = processIssueAnalysisData(rowData, prefix);

    setDashboardUserDetailsData((prev: any) => ({
      ...prev,
      issueAnalysis: [{ prefix: prefix }, { data: result }],
    }));
  };

  const getMissingColumns = (
    missing_records_details: any,
    resource_prefixes?: any,
  ) => {
    let finalColumns;
    const recordData =
      missing_records_details[0]?.missing_record_details?.record_snapshot
        ?.record_data;
    const resourceKeys = resource_prefixes ? resource_prefixes : [];

    // Get field-to-prefix mapping for header transformation
    const fieldToPrefixMap = getFieldToPrefixMapping(
      missing_records_details,
      dashboardData,
      "missing",
    );
    const resource_prifix_columns: any = resourceKeys.map((item: any) => {
      return {
        field: item,
        headerName: item,
        headerAlign: "center",
        align: "center",
        flex: 1,
        minWidth: 120,
        renderCell: (params: any) => {
          if (params.row[item] === "truepositive") {
            return <IconCheckCircleIconGreenSvg width={21} height={21} />;
          }
          if (params.row[item] === "secondaryfulfilled") {
            const secondaryResourceName = getSecondaryMergeResourceName(
              params.row[SECONDARY_MERGE_RESOURCES_KEY],
              item,
            );
            return (
              <Tooltip
                title={
                  <Typography sx={{ fontSize: "0.875rem" }}>
                    {`Fulfilled by secondary resource : ${secondaryResourceName}`}
                  </Typography>
                }
                placement="top"
                arrow
              >
                <span>
                  <IconSecondaryResourceSvg width={21} height={21} />
                </span>
              </Tooltip>
            );
          }
          if (params.row[item] === "falsepositive") {
            return (
              <>
                <IconFalsePositive />
              </>
            );
          }
          if (params.row[item] === "falsenegative") {
            return <IconCrossCircleIconSvg width={21} height={21} />;
          }
        },
      };
    });
    const message: any = {
      field: "message",
      headerName: "Message",
      flex: 1,
      minWidth: 350,
      headerAlign: "center",
      renderCell: (params: any) => {
        const regex = /;(?![\w\s])/g;
        return (
          <span className="text-ellipsis">
            <Tooltip
              title={
                <Typography sx={{ fontSize: "0.875rem" }}>
                  {params.value.replace(regex, "")}
                </Typography>
              }
              placement="top"
              className="tooltip-ellipsis"
            >
              {params.value.replace(regex, "")}
            </Tooltip>
          </span>
        );
      },
    };
    const daysInQueueColumn: any = [
      {
        field: "days_in_queue",
        headerName: "Age",
        flex: 1,
        minWidth: 110,
        renderCell: (params: any) => {
          return (
            <Box>
              {params?.row?.first_occurrence === null ? (
                (params.value ?? 0)
              ) : (
                <Tooltip
                  title={
                    <Typography
                      sx={{ fontSize: "0.875rem" }}
                    >{`First Occurrence : ${getFormattedDateTime(
                      params?.row?.first_occurrence,
                    )}`}</Typography>
                  }
                  placement="top"
                  className="tooltip-ellipsis"
                >
                  {params.value ?? 0}
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ];
    const resColumns: any = [
      CurrentStatusColumn({}),
      UserColumn({}),
      CommentColumn({}),
      ClassificationColumn({}),
      ReasonColumn({}),
      {
        field: "action",
        headerName: "Actions",
        align: "center",
        headerAlign: "center",
        minWidth: "130",
        pinned: "right",
        renderCell: (params: any, index: any) => {
          return (
            <>
              <Box sx={{ display: "inline-flex", columnGap: "12px" }}>
                <Tooltip title={`New Issue Details`} placement="top" arrow>
                  <Button className="btn-blue btn-orange btn-sm">
                    <IconBoltType />
                  </Button>
                </Tooltip>
                {canEditRuleExecutionIssues && (
                  <Tooltip
                    title={`Incident Details : ${params?.row?.id}`}
                    placement="top"
                    arrow
                  >
                    <Button
                      className="btn-blue btn-orange btn-sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        setDashboardUserDetailsData((prev: any) => ({
                          ...prev,
                          status: true,
                          incidentId: params.row.rowId,
                          assigned_user: params.row.assigned_user,
                          panelType: "incidentDetail",
                          isBackDropVisible: true,
                          first_occurrence: params.row.first_occurrence,
                          issue_classification_id:
                            params.row.issue_classification_id,
                          issue_reason_id: params.row.issue_reason_id,
                          comment: [
                            {
                              comment: params.row.comment,
                              create_date: params.row.create_date,
                              assigned_user: params.row?.assigned_user || "",
                            },
                          ],
                          current_status: params.row.current_status,
                          issueId: params.row.id,
                        }));
                      }}
                    >
                      <IconIncidentDetails />
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title="Issue Analysis" placement="top" arrow>
                  <Button
                    className="btn-blue btn-orange btn-sm"
                    onClick={(event) => {
                      event.stopPropagation();
                      setDashboardUserDetailsData((prev: any) => ({
                        ...prev,
                        status: true,
                        panelType: "incidentResource",
                        isBackDropVisible: true,
                      }));
                      handleIncidentResources(params?.row?.id);
                    }}
                  >
                    <IconIncidentResources />
                  </Button>
                </Tooltip>
              </Box>
            </>
          );
        },
      },
    ];
    const columns: any = Object.keys(
      missing_records_details[0]?.missing_record_details?.comparison_keys,
    ).map((item: any) => {
      const originalHeaderName = getSanatizedCode(item);
      const transformedHeaderName = transformHeaderName(
        item,
        originalHeaderName,
        fieldToPrefixMap,
      );
      return {
        field: item,
        headerName: transformedHeaderName.replace(/^Key_/, ""),
        flex: 1,
        minWidth: 100,
        headerAlign: "center",
        align: "center",
        renderCell: (params: any) => {
          const fileNames = Object.keys(params.row)
            .filter((key) => key.includes("_file_name"))
            .map((key) => params.row[key])
            .filter((fileName) => fileName);

          const fileNamesDisplay =
            fileNames.length > 0 ? (
              <div>
                <strong>File name(s):</strong>
                <br />
                {fileNames.map((fileName, index) => {
                  const tempElement = document.createElement("div");
                  tempElement.innerHTML = fileName;
                  const fileNamesText =
                    tempElement.textContent || tempElement.innerText;
                  const individualFileNames =
                    fileNamesText.split(/\.xlsx|\.xls/);
                  return individualFileNames.map((fileName, subIndex) => {
                    const trimmedFileName = fileName.trim();
                    if (trimmedFileName) {
                      return (
                        <li key={`${index}-${subIndex}`}>
                          {trimmedFileName}.xlsx
                        </li>
                      );
                    }
                    return null;
                  });
                })}
              </div>
            ) : null;
          return (
            <span className="text-ellipsis">
              <Tooltip
                title={
                  <Typography sx={{ fontSize: "0.875rem" }}>
                    <ul className="pl-20">{fileNamesDisplay}</ul>
                  </Typography>
                }
                placement="top"
                className="tooltip-ellipsis"
              >
                {params.value}
              </Tooltip>
            </span>
          );
        },
      };
    });

    // Enhanced grouping function to create distinct sections aligned with column grouping model
    const groupColumnsByPrefix = (columns: any[]) => {
      const prefixes =
        dashboardData?.additional_properties?.resource_prefixes || [];
      const resourceColumns: any[] = [];
      const prefixColumns: any[] = [];
      const commonColumns: any[] = [];

      // 1. First, collect base resource columns (exact matches with prefixes)
      prefixes.forEach((prefix: string) => {
        const baseColumn = columns.find((col) => col.field === prefix);
        if (baseColumn) {
          resourceColumns.push(baseColumn);
        }
      });

      // 2. Then, collect prefix-specific columns in organized order
      prefixes.forEach((prefix: string) => {
        // File name column (e.g., "Factset_file_name")
        const fileColumn = columns.find(
          (col) => col.field === `${prefix}_file_name`,
        );
        if (fileColumn) {
          prefixColumns.push(fileColumn);
        }

        // Other prefix-specific columns (excluding base, file_name, and Mismatched_)
        const otherPrefixColumns = columns.filter(
          (col) =>
            col.field.startsWith(`${prefix}_`) &&
            !col.field.endsWith("_file_name") &&
            !col.field.startsWith(`Mismatched_${prefix}_`),
        );
        prefixColumns.push(...otherPrefixColumns);

        // Mismatched value column (e.g., "Mismatched_Factset_Value")
        const mismatchedColumn = columns.find(
          (col) => col.field === `Mismatched_${prefix}_Value`,
        );
        if (mismatchedColumn) {
          prefixColumns.push(mismatchedColumn);
        }
      });

      // 3. Finally, collect columns that don't match any prefix pattern (common columns)
      columns.forEach((col) => {
        const isGrouped = prefixes.some(
          (prefix: string) =>
            col.field === prefix ||
            col.field.startsWith(`${prefix}_`) ||
            col.field === `Mismatched_${prefix}_Value`,
        );
        if (!isGrouped) {
          commonColumns.push(col);
        }
      });

      // Return in the order: Resources, Prefix-specific, Common
      return [...resourceColumns, ...prefixColumns, ...commonColumns];
    };

    if (recordData) {
      const snapshotColumns = recordData
        ? Object.keys(recordData).map((key) => {
            const dynamicWidth = calculateDynamicWidth(
              key,
              missing_records_details,
              "missing",
            );
            const transformedHeaderName = transformHeaderName(
              key,
              key,
              fieldToPrefixMap,
            );
            return {
              field: key,
              headerName: transformedHeaderName.replace(/^Key_/, ""),
              flex: 1,
              minWidth: dynamicWidth > 120 ? dynamicWidth : 120,
            };
          })
        : [];
      const comparisonKeys =
        missing_records_details[0]?.missing_record_details?.comparison_keys;
      const excludedFields = ["Error_Message", "Missing_Resources_Prefixes"];
      const filtredSnapShot = snapshotColumns.filter(
        (column) =>
          !Object.keys(comparisonKeys).includes(column.field) &&
          !excludedFields.includes(column.field),
      );

      // For missing records, resource_prifix_columns ARE the base resource columns
      // So we need to separate them from other columns for proper grouping
      const dataColumns = [...filtredSnapShot]; // Only snapshot columns for prefix grouping
      const groupedDataColumns = groupColumnsByPrefix(dataColumns);

      finalColumns = [
        ...columns, // 1. Key columns (dynamic count, typically 2)
        message, // 3. Message column (now positioned immediately after resources)
        ...resource_prifix_columns, // 2. Resource columns (dynamic count based on prefixes)
        ...groupedDataColumns, // 4. Prefix-specific grouped columns
        ...daysInQueueColumn, // 5. Days in queue column
        ...resColumns, // 6. Action columns (status, user, comment, action)
      ];
    } else {
      finalColumns = [
        ...columns, // 1. Key columns
        message, // 3. Message column (positioned right after resources)
        ...resource_prifix_columns, // 2. Resource columns (Resources group)
        ...daysInQueueColumn, // 4. Days in queue column
        ...resColumns, // 5. Action columns
      ];
    }
    // Ensure no duplicate columns exist based on the "field" property
    const deduplicatedColumns = deduplicateByFieldProperty(
      finalColumns,
      "field",
    );
    setMissingColumnsData(excludeInternalResultColumns(deduplicatedColumns));
  };

  useEffect(() => {
    // missing records
    if (
      missingResultsRuleDashboardData?.items?.length > 0 &&
      dashboardData?.additional_properties?.resource_prefixes
    ) {
      getMissingColumns(
        missingResultsRuleDashboardData?.items,
        dashboardData?.additional_properties?.resource_prefixes,
      );
      getMissingRows(
        missingResultsRuleDashboardData?.items,
        dashboardData?.additional_properties?.resource_prefixes,
      );
    }
  }, [missingResultsRuleDashboardData, dashboardData]);
  React.useEffect(() => {
    setUserDialogData((prev: any) => ({
      ...prev,
      selectedIds,
    }));
  }, [selectedIds]);
  const [pinnedColumns, setPinnedColumns] = useState({
    left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
    right: ["current_status", "username", "comment", "action"],
  });

  useEffect(() => {
    setPinnedColumns((prevState) => {
      const updatedLeft = [
        GRID_CHECKBOX_SELECTION_COL_DEF.field,
        ...keyColumns,
      ];
      const updatedRight = [...prevState.right];

      return {
        left: updatedLeft,
        right: updatedRight,
      };
    });
  }, [keyColumns]);

  const handleMoveColumns = () => {
    setPinnedColumns((prevState) => {
      const columnsToAdd = ["current_status", "username", "comment"];
      const isColumnsAvailable = columnsToAdd.every((item) =>
        prevState.right.includes(item),
      );
      const isKeyColumnAvailable = keyColumns.every((item: any) =>
        prevState.left.includes(item),
      );
      setIsAllColumnPinned(isColumnsAvailable);
      if (isColumnsAvailable && isKeyColumnAvailable) {
        return {
          left: prevState.left.filter((item) => !keyColumns.includes(item)),
          right: prevState.right.filter((item) => !columnsToAdd.includes(item)),
        };
      } else {
        return {
          left: [...prevState.left, ...keyColumns],
          right: [...columnsToAdd, ...prevState.right],
        };
      }
    });
  };

  return (
    <>
      {keyColumns && (
        <>
          <Box className="position-relative">
            {isMissingLoading && (
              <Box className="adhock-query-accordion-wrapper">
                <Loader
                  isAbsolute={true}
                  radius={{ top: 0, right: 0, bottom: 5, left: 5 }}
                  isLoading={isMissingLoading}
                />
              </Box>
            )}
            {missingRowsData.length > 0 && (
              <IconButton
                onClick={handleMoveColumns}
                className={`icon-pin-datagrid navbar-icon burger  ${
                  isAllColumnPinned ? "active" : ""
                }`}
              >
                <div className="bar bar1"></div>
                <div className="bar bar2"></div>
                <div className="bar bar3"></div>
              </IconButton>
            )}
            {missingRowsData && missingRowsData?.length !== 0 ? (
              <Fade in timeout={300}>
                <Box>
                  <RulesListDataTable
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                    dataRows={missingRowsData || []}
                    dataColumns={missingColumnsData || []}
                    loading={isMissingLoading || isLoading}
                    disableColumnFilter={true}
                    className="dataTable datagridHeaderColumn no-radius pt-0 bdr-top-0  datatable-column-sep checkbox-result-column pinnedColumnHeaders-bg-height hide-progress-icon"
                    tableHeight={180}
                    disableColumnReorder={true}
                    columnGroupingModel={columnGroupingModel}
                    setResultColumns={setMissingColumns}
                    checkboxSelection={
                      missingRowsData?.length > 0 ? true : false
                    }
                    paginationMode="server"
                    rowCount={missingResultsRuleDashboardData?.total || 0}
                    pageSizeOptions={rowsPerPageOptions}
                    paginationModel={{
                      page: page - 1,
                      pageSize: pSize,
                    }}
                    onPaginationModelChange={(params: any) => {
                      if (
                        params.pageSize !== pSize ||
                        params.page !== page - 1
                      ) {
                        setPage(params.page + 1);
                        setPSize(params.pageSize);
                      }
                    }}
                    pinnedColumns={
                      missingRowsData?.length > 0 ? pinnedColumns : {}
                    }
                    disableColumnPinning={false}
                    singlePageMaxHeightDiff={262}
                    variant="premium"
                    resourcePrefixes={
                      dashboardData?.additional_properties?.resource_prefixes
                    }
                  />
                </Box>
              </Fade>
            ) : (
              <Box
                className="adhock-query-accordion-wrapper"
                sx={{
                  paddingTop: "24px",
                  paddingBottom: "24px",
                  display: "flex",
                  justifyContent: "center",
                  borderTop: "none",
                  // Dark mode: match the premium inset surface so the empty
                  // state blends with the surrounding card rather than
                  // appearing as an untreated white void.
                  backgroundColor: isDark
                    ? darkModeTokens.surfaceInset
                    : "transparent",
                  color: "text.secondary",
                }}
              >
                No data available
              </Box>
            )}
          </Box>
        </>
      )}
      {/* for single update  */}
      <DashboardUserDetails
        dashboardUserDetailsData={dashboardUserDetailsData}
        setDashboardUserDetailsData={setDashboardUserDetailsData}
        allUsersList={allUsersList}
        setIsLoading={setIsLoading}
        setDetailViewDataRows={setMissingRowsData}
      />
      {/* for bulk update */}
      <IncidentUserandStatusDialog
        userDialogData={userDialogData}
        setUserDialogData={setUserDialogData}
        allUsersList={allUsersList}
        setDetailViewDataRows={setMissingRowsData}
        setSelectedIds={setSelectedIds}
      />
    </>
  );
};

export default MissingRecordsDashboardTab;
